<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useChatStore } from '../stores/chat'

const props = withDefaults(defineProps<{ whatsappUrl?: string }>(), { whatsappUrl: '#' })
const chat = useChatStore()
const draft = ref('')
const list = ref<HTMLElement | null>(null)
const showResume = ref(false)
const resumePhone = ref('')
const resumeCode = ref('')
const assistantSubtitle = computed(() => chat.professionalName
  ? `Assistente virtual de ${chat.professionalName}`
  : 'Assistente virtual da profissional')
const directContactLabel = computed(() => chat.professionalName
  ? `Falar diretamente com ${chat.professionalName} pelo WhatsApp`
  : 'Falar diretamente com a profissional pelo WhatsApp')

async function scrollToLatest(behavior: ScrollBehavior = 'smooth'): Promise<void> {
  await nextTick()
  requestAnimationFrame(() => {
    list.value?.scrollTo({ top: list.value.scrollHeight, behavior })
  })
}

watch(
  [() => chat.messages.length, () => chat.isSending, () => chat.isOpen],
  () => { void scrollToLatest('auto') },
  { flush: 'post' },
)

async function submit(): Promise<void> {
  const message = draft.value
  draft.value = ''
  await chat.send(message)
}

async function submitResume(): Promise<void> {
  const resumed = await chat.resume(resumePhone.value, resumeCode.value)
  if (resumed) {
    showResume.value = false
    resumeCode.value = ''
    await scrollToLatest()
  }
}
</script>

<template>
  <button
    class="fixed bottom-5 right-5 z-50 grid size-16 place-items-center rounded-full border border-white/15 bg-[#8c1838] text-2xl text-white shadow-[0_20px_60px_rgba(140,24,56,.45)] transition hover:scale-105"
    type="button"
    aria-label="Abrir Terapeut-IA®"
    @click="chat.isOpen = true"
  >
    ✦
  </button>

  <Transition name="chat">
    <section
      v-if="chat.isOpen"
      class="fixed inset-3 z-50 flex flex-col overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#12070c] shadow-2xl sm:inset-auto sm:bottom-24 sm:right-5 sm:h-[640px] sm:w-[390px]"
    >
      <header class="flex items-center gap-3 border-b border-white/10 bg-[#1b0a11] px-5 py-4">
        <div class="grid size-11 place-items-center rounded-full bg-[#8c1838] text-xl">✦</div>
        <div class="min-w-0 flex-1">
          <strong class="block text-white">Terapeut-IA®</strong>
          <span class="text-xs text-white/50">{{ assistantSubtitle }}</span>
        </div>
        <button class="text-xl text-white/60 hover:text-white" type="button" @click="chat.isOpen = false">×</button>
      </header>

      <div ref="list" class="flex-1 space-y-3 overflow-y-auto p-4">
        <div
          v-for="message in chat.messages"
          :key="message.id"
          class="max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-6"
          :class="message.role === 'user' ? 'ml-auto bg-[#8c1838] text-white' : 'bg-white/[.06] text-white/80'"
        >
          {{ message.content }}
        </div>
        <div v-if="chat.isSending" class="w-fit rounded-2xl bg-white/[.06] px-4 py-3 text-sm text-white/55">
          Preparando resposta…
        </div>
      </div>

      <div class="border-t border-white/10 p-4">
        <div v-if="!chat.continuityAuthenticated" class="mb-3">
          <button
            class="block w-full text-center text-xs text-white/55 hover:text-white"
            type="button"
            @click="showResume = !showResume"
          >
            Já fui atendido — retomar histórico
          </button>
          <form v-if="showResume" class="mt-3 space-y-2 rounded-2xl border border-white/10 bg-white/[.03] p-3" @submit.prevent="submitResume">
            <p class="text-xs leading-5 text-white/55">Confirme sua identidade com os dados do agendamento mais recente.</p>
            <input
              v-model="resumePhone"
              class="w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#ad3155]"
              inputmode="tel"
              autocomplete="tel"
              placeholder="Telefone"
              required
            />
            <input
              v-model="resumeCode"
              class="w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#ad3155]"
              autocomplete="off"
              placeholder="Código do último agendamento"
              minlength="6"
              maxlength="20"
              required
            />
            <p v-if="chat.resumeError" class="text-xs leading-5 text-rose-300">{{ chat.resumeError }}</p>
            <button class="w-full rounded-xl bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/15 disabled:opacity-50" type="submit" :disabled="chat.isResuming">
              {{ chat.isResuming ? 'Confirmando…' : 'Retomar conversa' }}
            </button>
          </form>
        </div>
        <p v-else class="mb-3 text-center text-xs text-emerald-300/80">
          Histórico confirmado<span v-if="chat.clientName"> para {{ chat.clientName }}</span>.
        </p>
        <form class="flex gap-2" @submit.prevent="submit">
          <input
            v-model="draft"
            class="min-w-0 flex-1 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#ad3155]"
            placeholder="Digite sua mensagem..."
            :disabled="!chat.canSend"
          />
          <button class="grid size-12 place-items-center rounded-2xl bg-[#8c1838] text-white disabled:opacity-50" :disabled="!draft.trim() || !chat.canSend">
            ➤
          </button>
        </form>
        <a class="mt-3 block text-center text-xs text-white/45 hover:text-white" :href="props.whatsappUrl" target="_blank" rel="noreferrer">
          {{ directContactLabel }}
        </a>
      </div>
    </section>
  </Transition>
</template>
